import React, { Fragment, ReactNode, useRef, useState } from "react";
import { Button, RadioGroup } from "@telia/teddy";
import _cloneDeep from "lodash/cloneDeep";
import _isEmpty from "lodash/isEmpty";
import _sortBy from "lodash/sortBy";
import { useSelector } from "react-redux";
import { NEARBY_SHOP_THRESHOLD_KM } from "../../../../../constants";
import { RootState } from "../../../../../models";
import { ShopDetails, ShopDetailsWithDistance, ShopLocation, Shops } from "../../../../../models/state";
import {
    getOnboardingMobileSupportInfo,
    getSelectedOnboardingSku,
} from "../../../checkout/state/selectors/deliveryInfoSelectors";
import { Alert, ProgressBar } from "../../../components";
import TEXT from "../../text-consts";
import { getNearbyShopsFromLocation, flattenShopAvailability } from "../../utils";
import GeoLocation from "../atoms/GeoLocation/GeoLocation";
import OnboardingMobileSupportInfo from "../atoms/OnboardingMobileSupportInfo/OnboardingMobileSupportInfo";
import OnboardingMobileSupportModal from "../atoms/OnboardingMobileSupportModal/OnboardingMobileSupportModal";
import PickupInStoreShop from "../atoms/PickupInStoreShop/PickupInStoreShop";
import Search from "../atoms/Search/Search";
import "./ShopPicker.less";

const NEARBY_LOCATIONS_TO_DISPLAY_AT_ONCE = 3;

interface ShopPickerContentProps {
    numberOfNearbyShopsToDisplay?: number;
    onShopSelected: (shop?: ShopDetails) => void;
    selectedShop?: ShopDetails;
    shopAvailability: Shops;
}

const ShopPickerContent: React.FC<ShopPickerContentProps> = ({
    shopAvailability,
    numberOfNearbyShopsToDisplay,
    onShopSelected,
    selectedShop,
}) => {
    const [nearbyShops, setNearbyShops] = useState<ShopDetailsWithDistance[]>([]);
    const [numberOfNearbyShopsToDisplayState, setNumberOfNearbyShopsToDisplayState] = useState(
        numberOfNearbyShopsToDisplay ?? NEARBY_LOCATIONS_TO_DISPLAY_AT_ONCE,
    );
    const selectedOnboardingSku = useSelector((state: RootState) => getSelectedOnboardingSku(state));
    const [showOnboardingModal, setShowOnboardingModal] = useState(false);
    const onboardingMobileSupportInfo = useSelector((state: RootState) => getOnboardingMobileSupportInfo(state));
    const allAvailableShops = flattenShopAvailability(shopAvailability);
    const [displayAllShops, setDisplayAllShops] = useState(allAvailableShops.length === 1);
    const [displayLocationError, setDisplayLocationError] = useState(false);
    const [displayNoNearbyShopsMsg, setDisplayNoNearbyShopsMsg] = useState(false);
    const [displayFindingNearbyShopsLoader, setDisplayFindingNearbyShopsLoader] = useState(false);

    const refShopPicker = useRef<HTMLElement>(null);

    const showOnboardingInfo = selectedOnboardingSku === "";

    const setShopSelected = (shop: ShopDetails) => {
        setDisplayAllShops(false);
        setDisplayLocationError(false);
        setNumberOfNearbyShopsToDisplayState(NEARBY_LOCATIONS_TO_DISPLAY_AT_ONCE);

        // Scroll to top of ShopPickerContent
        if (refShopPicker.current !== null) {
            window.scrollTo(0, refShopPicker.current.offsetTop);
        }

        onShopSelected(shop);
    };

    const onShopCodeSelected = (code: string) => {
        const shop = allAvailableShops.find((availableShop) => availableShop.code === code);
        if (shop) {
            setShopSelected(shop);
        }
    };

    const setSelectedSearchLocation = (location?: ShopLocation | null) => {
        if (location) {
            // Get nearby shops
            const newNearbyShops: ShopDetailsWithDistance[] = getNearbyShopsFromLocation(
                location,
                shopAvailability,
                NEARBY_SHOP_THRESHOLD_KM,
            );

            // Sort them by distance
            newNearbyShops.sort((a, b) => a.distance - b.distance);

            setNearbyShops(newNearbyShops);
            setNumberOfNearbyShopsToDisplayState(NEARBY_LOCATIONS_TO_DISPLAY_AT_ONCE);
            setDisplayAllShops(false);
            setDisplayLocationError(false);
            setDisplayNoNearbyShopsMsg(newNearbyShops.length === 0);
        } else {
            setNearbyShops([]);
            setDisplayLocationError(false);
        }
    };

    const renderCollapsibleShop = (shop?: ShopDetails): JSX.Element => (
        <Fragment key={`collapsible-shop-${shop?.code ?? "none"}`}>
            {shop && (
                <>
                    <PickupInStoreShop {...shop} selectedShop={selectedShop} />
                </>
            )}
        </Fragment>
    );

    const renderAllShops = () => {
        // Sort them by name
        return (
            <RadioGroup value={selectedShop?.code || ""} onValueChange={onShopCodeSelected} name="clickCollectShop">
                {_sortBy(allAvailableShops, [(shop) => shop.name]).map((shop) => renderCollapsibleShop(shop))}
            </RadioGroup>
        );
    };

    const replaceLastShopWithSelectedShop = (shopsToRender: React.ReactNode[]) => {
        const newShops = _cloneDeep(shopsToRender);
        // Replace last shop in array with selected shop
        if (newShops.length >= NEARBY_LOCATIONS_TO_DISPLAY_AT_ONCE) {
            newShops.pop();
        }
        newShops.push(renderCollapsibleShop(selectedShop));
        return newShops;
    };

    const renderNearbyShops = () => {
        let includesSelectedShop = false;
        const shopsToRender = nearbyShops.reduce((arr, shop, index) => {
            if (index < numberOfNearbyShopsToDisplayState) {
                if (shop.code === selectedShop?.code) {
                    includesSelectedShop = true;
                }
                arr.push(renderCollapsibleShop(shop));
            }
            return arr;
        }, [] as ReactNode[]);
        const shops =
            selectedShop && !includesSelectedShop ? replaceLastShopWithSelectedShop(shopsToRender) : shopsToRender;
        return (
            <RadioGroup value={selectedShop?.code || ""} onValueChange={onShopCodeSelected} name="clickCollectShop">
                {shops}
            </RadioGroup>
        );
    };

    const renderShowAllAvailableShops = () => {
        const onClick = () => {
            setDisplayAllShops(!displayAllShops);
            setDisplayLocationError(false);
            setNearbyShops([]);
            setDisplayNoNearbyShopsMsg(false);
        };

        return (
            <div className="shop-picker__show-all">
                <Button onClick={onClick} variant="text-underline">
                    {TEXT.VIEW_ALL((allAvailableShops || []).length)}
                </Button>
            </div>
        );
    };

    const renderShowMoreNearbyShops = () => {
        const showMoreStores = () => {
            setNumberOfNearbyShopsToDisplayState(
                numberOfNearbyShopsToDisplayState + NEARBY_LOCATIONS_TO_DISPLAY_AT_ONCE,
            );
        };
        const showLessStores = () => {
            setNumberOfNearbyShopsToDisplayState(NEARBY_LOCATIONS_TO_DISPLAY_AT_ONCE);
        };

        return (
            <div className="shop-picker__show-more">
                {numberOfNearbyShopsToDisplayState < nearbyShops.length && (
                    <Button onClick={showMoreStores} variant="text-underline">
                        {TEXT.SHOW_MORE_STORES(NEARBY_LOCATIONS_TO_DISPLAY_AT_ONCE)}
                    </Button>
                )}
                {numberOfNearbyShopsToDisplayState > NEARBY_LOCATIONS_TO_DISPLAY_AT_ONCE && (
                    <Button onClick={showLessStores} variant="text-underline">
                        {TEXT.SHOW_LESS_STORES}
                    </Button>
                )}
            </div>
        );
    };

    const renderGeoLocation = () => {
        const onClick = () => {
            setDisplayNoNearbyShopsMsg(false);
            setDisplayFindingNearbyShopsLoader(true);
        };

        const onNotAvailable = () => {
            setDisplayLocationError(true);
            setDisplayAllShops(false);
            setNearbyShops([]);
            setDisplayNoNearbyShopsMsg(false);
            setDisplayFindingNearbyShopsLoader(false);
        };

        const success = (position: GeolocationPosition) => {
            setDisplayFindingNearbyShopsLoader(false);

            setSelectedSearchLocation({
                lat: position.coords.latitude,
                lng: position.coords.longitude,
            });
        };

        const error = (err: GeolocationPositionError) => {
            switch (err.code) {
                case err.PERMISSION_DENIED:
                case err.POSITION_UNAVAILABLE:
                case err.TIMEOUT:
                    onNotAvailable();
                    break;
            }
        };

        return (
            <GeoLocation
                className="shop-picker__search-container__aside"
                onClick={onClick}
                onNotAvailable={onNotAvailable}
                success={success}
                error={error}
            />
        );
    };

    const renderOnboardingMobileSupport = () => {
        return <OnboardingMobileSupportInfo setShowModal={(): void => setShowOnboardingModal(true)} />;
    };

    return (
        <section ref={refShopPicker} className="shop-picker">
            <div className="shop-picker__content">
                <div className="shop-picker__search-container">
                    <div className="shop-picker__search-container__main">
                        <Search setSelectedSearchLocation={setSelectedSearchLocation} />
                        {displayNoNearbyShopsMsg && <p>{TEXT.ERROR.NO_NEARBY_SHOP}</p>}
                        <ProgressBar isFetchingData={displayFindingNearbyShopsLoader} backgroundColor="white" />
                        {renderShowAllAvailableShops()}
                    </div>
                    {renderGeoLocation()}
                </div>
                {displayLocationError && (
                    <div className="shop-picker__alert">
                        <Alert kind="info">{TEXT.ERROR.LOCATION_NOT_AVAILABLE}</Alert>
                    </div>
                )}
                {!displayAllShops && renderNearbyShops()}
                {displayAllShops && renderAllShops()}
                {!_isEmpty(nearbyShops) && renderShowMoreNearbyShops()}
                {showOnboardingInfo && selectedShop && onboardingMobileSupportInfo && renderOnboardingMobileSupport()}
                {onboardingMobileSupportInfo && (
                    <OnboardingMobileSupportModal
                        showModal={showOnboardingModal && !!onboardingMobileSupportInfo}
                        setShowModal={setShowOnboardingModal}
                        onboardingMobileSupportInfo={onboardingMobileSupportInfo}
                    />
                )}
            </div>
        </section>
    );
};

export default ShopPickerContent;
